import { format } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import { GridSortModel } from "@mui/x-data-grid";
import IKanbanBoardProductionSchemaProps from "../../../Components/KanbanBoardProduction/interfaces/kanban-board-production-schema.interface";
import { Environment } from "../../../Config/constants";
import { useToast } from "../../../Hooks/ToasContext";
import { BillingFlowEnum, IBillProductOrderPayload, IOrderEntity, IOrdersListPayload, TBillingFlow } from "../../../Services/api/orders/orders.interface";
import { ServiceOrderStatusEnum } from "../../../Services/api/service-orders/service-order.interface";
import { BillProductOrderOrdersUseCase } from "../../../Usecase/Orders/bill-products-order-usecase";
import { ListOrdersUseCase } from "../../../Usecase/Orders/list-orders-usecase";
import { Task } from "../Details/financial-details.hook";
import { ReprocessOrderPaymentsUseCase } from "../../../Usecase/OrderPayments/reprocess-order-payments-usecase";
import { useAuth } from "../../../Hooks/Auth";
import { UpdateOrdersUseCase } from "../../../Usecase/Orders/update-order-usecase";
import { getOrderBillingFlow } from "../../../utils/billingFlow";
import { mapApiOrderFieldToGrid, mapGridOrderFieldToApi } from "../../../utils/order-list-sort";


interface IUseListFinancialProps {
  listOrdersUseCase: ListOrdersUseCase
  billProductOrderOrdersUseCase: BillProductOrderOrdersUseCase
  reprocessOrderPaymentsUseCase: ReprocessOrderPaymentsUseCase  
  updateOrdersUseCase: UpdateOrdersUseCase
}
export function useListFinancial({ listOrdersUseCase, billProductOrderOrdersUseCase, reprocessOrderPaymentsUseCase, updateOrdersUseCase }: IUseListFinancialProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [page, setPage] = useState(1)
    const [data, setData] = useState<IOrderEntity[]>([])
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [showDetails, setShowDetails] = useState<undefined | string>(undefined)
    const [showFiltersModal, setShowFiltersModal] = useState(false)
    const [kanbanSchema, setKanbanSchema] = useState<IKanbanBoardProductionSchemaProps>()
    const [orderSelectedToBill, setOrderSelectedToBill] = useState<IOrderEntity | undefined>(undefined)
    const [currentFilters, setCurrentFilters] = useState<IOrdersListPayload>({ page: 1 })
    const currentFiltersRef = useRef<IOrdersListPayload>({ page: 1 })
    const [orderToReprocess, setOrderToReprocess] = useState<string | undefined>(undefined)
    const { user } = useAuth()
   


    const hasReprocessPermission = useMemo(() => {
      return user?.permissionsScreens?.includes(`feature:orders-reprocess:manage`)
    }, [user])
    
    
   

    const { showToast } =  useToast()

    const buildListFilters = useCallback((payload: IOrdersListPayload): IOrdersListPayload => {
      const previousFilters = currentFiltersRef.current
      const filters: IOrdersListPayload = {
        ...previousFilters,
        ...payload,
        page: payload.page || previousFilters.page || 1,
        searchBy: 'searchBy' in payload ? payload.searchBy : previousFilters.searchBy,
        status: 'status' in payload ? payload.status : previousFilters.status,
        productionStatus: 'productionStatus' in payload ? payload.productionStatus : previousFilters.productionStatus,
        store: 'store' in payload ? payload.store : previousFilters.store,
        startDate: 'startDate' in payload ? payload.startDate : previousFilters.startDate,
        endDate: 'endDate' in payload ? payload.endDate : previousFilters.endDate,
        orderBy: 'orderBy' in payload ? payload.orderBy : previousFilters.orderBy,
      }

      if (!filters.searchBy?.trim()) {
        delete filters.searchBy
      }

      return filters
    }, [])

    const transformData = (dados: IOrderEntity[]) => {
      
      const tasks: Record<string, Task> = {};
      const readyToInvoice : any = [];
      const invoiced : any = [];
      const finishsedOS : any = [];

   
      
      dados.forEach((item, index) => {
          // const taskId = `task-${index + 1}`;

          item.products.forEach(product => {
            const taskId = item.id;
            tasks[taskId] = {
                id: taskId,
                content: {
                    os: product.id,
                    items: []
                    
                }
            };

            if(product.status === ServiceOrderStatusEnum.PRODUCED){
              readyToInvoice.push(taskId);
            } else if (product.status === ServiceOrderStatusEnum.BILLED){
              invoiced.push(taskId)
            } 
          })
          
          
      });

      const result = {
          tasks,
          columns: {
            [ServiceOrderStatusEnum.PRODUCED]: {
                  id: ServiceOrderStatusEnum.PRODUCED,
                  title: 'Aguardando Faturamento',
                  taskIds: readyToInvoice,
              },
              [ServiceOrderStatusEnum.PROCESSING]: {
                  id: ServiceOrderStatusEnum.PROCESSING,
                  title: 'Faturado',
                  taskIds: invoiced,
              }
          },
          columnOrder: [ServiceOrderStatusEnum.INITIAL, ServiceOrderStatusEnum.PROCESSING, ServiceOrderStatusEnum.PRODUCED],
      };

      return result;
    }

    const listOrders = useCallback(async (payload: IOrdersListPayload) => {
      try {
        setLoading(true)
        setLoadError(false)

        const nextFilters = buildListFilters(payload)
        currentFiltersRef.current = nextFilters
        setCurrentFilters(nextFilters)

        const listResult = await listOrdersUseCase.handle(nextFilters);

        if (listResult.isFailure) {
          setLoadError(true)
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        const list = listResult.getValue()

        if (list?.data) {
          setData(list.data)
          setTotalRegisters(list.total)
          setLoadError(false)
        }

        setPage(nextFilters.page || 1)
      } catch (error) {
        setLoadError(true)
        console.error("error listSales function: ", error)
      } finally {
        setLoading(false);
      }
  }, [buildListFilters, listOrdersUseCase, showToast]);

  const handlePageChangeOrders = useCallback((data: IOrdersListPayload) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    listOrders(data)
  }, [listOrders]);

  const handleSearch = useCallback((payload: IOrdersListPayload) => {
    listOrders({ ...currentFiltersRef.current, searchBy: payload.searchBy, page: payload.page ?? 1 })
  }, [listOrders]);

   
    
    const list = useMemo(() => {
        const rows = data?.map(sale => {

            return {
                ...sale,
                createdAt: format(new Date(sale.createdAt), 'dd/MM/yyyy'),
                totalValue: sale?.totalValue ?? '0',
                products: sale.products.map(item => {
                  return {
                    ...item,
                    
                    image: item?.product?.images[0]?.link || Environment.IMAGE_PLACEHOLDER
                  }
                })
              
            }
        })

        return rows
    }, [data])

    const handleShowDetails = (code: string) => {
      setShowDetails(code)
    }

    const financialSortModel = useMemo<GridSortModel>(() => {
      const orderBy = currentFilters.orderBy
      if (!orderBy?.field || !orderBy?.order) return []

      return [{
        field: mapApiOrderFieldToGrid(orderBy.field),
        sort: orderBy.order.toLowerCase() as 'asc' | 'desc',
      }]
    }, [currentFilters.orderBy])

    const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0]
        if (!sort) return

        listOrders({
          ...currentFilters,
          page: 1,
          orderBy: {
            field: mapGridOrderFieldToApi(field),
            order: sort.toUpperCase() as 'ASC' | 'DESC',
          },
        })
        return
      }

      listOrders({ ...currentFilters, orderBy: undefined, page: 1 })
    }, [currentFilters, listOrders])

    const billProductsOrder = async (reworkObservations?: string) => {
         if(!orderSelectedToBill){
            showToast('Selecione um pedido para faturar', 'error');
            return;
         }

         const currentBillingFlow = getOrderBillingFlow(orderSelectedToBill)
         const normalizedReworkObservations = reworkObservations?.trim()

         if (currentBillingFlow === BillingFlowEnum.REWORK && !normalizedReworkObservations) {
            showToast('Preencha as observações para faturar como retrabalho', 'error');
            return;
         }

      try {
        setLoading(true)

        if (currentBillingFlow === BillingFlowEnum.REWORK && normalizedReworkObservations !== orderSelectedToBill.observations) {
          const updateResult = await updateOrdersUseCase.handle({
            orderId: orderSelectedToBill.id,
            observations: normalizedReworkObservations,
          })

          if (updateResult.isFailure) {
            showToast(String(updateResult?.error?.error), 'error');
            return;
          }

          setOrderSelectedToBill(prev => prev ? { ...prev, observations: normalizedReworkObservations } : prev)
          setData(prev => prev.map(order => order.id === orderSelectedToBill.id ? { ...order, observations: normalizedReworkObservations } : order))
        }

        const payload : IBillProductOrderPayload = {
          orderId: orderSelectedToBill.id,
          productsIds: orderSelectedToBill.products.map(item => item.id)
        }
        
        const billResult = await billProductOrderOrdersUseCase.handle(payload);

        if (billResult.isFailure) {
     
          showToast(String(billResult?.error?.error), 'error');
          return;
        }

        showToast('Produtos faturados com sucesso', 'success');
        setOrderSelectedToBill(undefined)
        
      } catch (error) {
        console.log("error billProductsOrder function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleFilterSelect = useCallback((payload: IOrdersListPayload) => {
      listOrders({ ...payload, page: 1 })
    }, [listOrders]);

    const handleReprocessPayment = async (orderId: string) => {
      try {
        setLoading(true)
        
       const response =  await reprocessOrderPaymentsUseCase.handle(orderId);

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }

        setOrderToReprocess(undefined)
        showToast('Pagamento reprocessado com sucesso', 'success');
        return;
      } catch (error) {
        console.log("error listSales function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateSelectedOrderBillingFlow = async (billingFlow: TBillingFlow) => {
      if (!orderSelectedToBill?.id) return;

      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ orderId: orderSelectedToBill.id, billingFlow });

        if (result.isFailure) {
          showToast(String(result?.error?.error), 'error');
          return;
        }

        setOrderSelectedToBill(prev => prev ? { ...prev, billingFlow } : prev)
        setData(prev => prev.map(order => order.id === orderSelectedToBill.id ? { ...order, billingFlow } : order))
        showToast('Fluxo de faturamento atualizado com sucesso', 'success')
      } catch (error) {
        console.log("error handleUpdateSelectedOrderBillingFlow function: ", error)
        showToast('Erro ao atualizar fluxo de faturamento', 'error')
      } finally {
        setLoading(false)
      }
    }


 

      

    return {
        loading,
        loadError,
        listOrders,
        list,
        data,
        handleShowDetails,
        showDetails,
        setShowDetails,
        setShowFiltersModal,
        showFiltersModal,
        kanbanSchema,
        billProductsOrder,
        totalRegisters,
        orderSelectedToBill,
        setOrderSelectedToBill,
        handleFilterSelect,
        currentFilters,
        handleReprocessPayment,
        orderToReprocess,
        setOrderToReprocess,
        hasReprocessPermission,
        handlePageChangeOrders,
        handleUpdateSelectedOrderBillingFlow,
        handleSearch,
        financialSortModel,
        handleSortModelChange,
    }
}
